import React, { useState, useMemo, useCallback, useEffect, useRef, forwardRef, RefObject, useImperativeHandle } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { VStack, Checkbox } from '@musinsa/mwms-ds'
import { colors, colorSystem, zIndexSystem } from '@/styles/theme'
import Input from '@/components/content/Input'
import { SelectOptionType } from './SelectBoxTypes'
import useClickOutside from '@/hooks/useClickOutside'

export interface SearchableMultipleSelectProps<T = string | number | boolean> {
  className?: string
  options: SelectOptionType<T>[]
  values?: T[]
  value?: T[] // Controller에서 사용할 value prop 추가
  placeholder?: string
  separator?: string
  maxHeight?: number
  onlySelectAllMode?: boolean
  disableSelectAll?: boolean
  disabled?: boolean
  /** 드롭다운을 body 포털로 렌더 — overflow:hidden 컨테이너(예: FilterListV2) 안에서 잘리지 않게 한다 */
  usePortal?: boolean
  /**
   * 입력란을 '검색 필터 전용'으로 동작시킨다(기본 false).
   * - false(기존): 입력 텍스트(콤마 구분 라벨)를 선택값으로 동기화 — 타이핑으로 선택 가능
   * - true: 입력은 옵션 필터링에만 사용하고 선택/해제는 체크박스로만. 선택해도 검색어 유지 → 검색 기반 다중선택·전체선택 가능
   */
  filterOnlySearch?: boolean
  onChange?: (values: T[]) => void
  onInputChange?: (inputText: string) => void
  /** 검색 입력에서 Enter 시 호출 — 페이지 검색(필터 적용) 트리거용 */
  onEnter?: () => void
}

function SearchableMultipleSelectInner<T = string | number | boolean>(
  props: SearchableMultipleSelectProps<T>,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) {
  const {
    className,
    options = [],
    values: propValues,
    value, // Controller에서 전달되는 value
    placeholder = '옵션을 선택하거나 검색하세요',
    separator = ', ',
    maxHeight = 300,
    onlySelectAllMode = false,
    disableSelectAll = false,
    disabled = false,
    usePortal = false,
    filterOnlySearch = false,
    onChange,
    onInputChange,
    onEnter,
  } = props

  const values = value !== undefined ? value : propValues || []

  const [inputText, setInputText] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({})

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // useImperativeHandle을 사용하여 ref 처리
  useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement)

  const optionMap = useMemo(() => {
    const map = new Map<string, SelectOptionType<T>>()
    options.forEach((option) => {
      map.set(String(option.value), option)
    })
    return map
  }, [options])

  // 선택된 값들을 텍스트로 변환
  const selectedText = useMemo(() => {
    return values.map((value) => optionMap.get(String(value))?.label || String(value)).join(separator)
  }, [values, optionMap, separator])

  const displayText = onlySelectAllMode ? '전체 (수정불가)' : inputText || selectedText

  // 검색어 추출 (검색 전용 모드는 입력 전체, 아니면 마지막 콤마 이후의 텍스트)
  const searchTerm = useMemo(() => {
    if (!inputText) return ''
    if (filterOnlySearch) return inputText.trim()

    const lastCommaIndex = inputText.lastIndexOf(',')
    if (lastCommaIndex === -1) {
      return inputText.trim()
    }
    return inputText.substring(lastCommaIndex + 1).trim()
  }, [inputText, filterOnlySearch])

  // 콤마로 끝나는지 확인 (새 항목 추가 의도) — 검색 전용 모드에서는 사용 안 함
  const isAddingNew = useMemo(() => {
    if (filterOnlySearch) return false
    return inputText.endsWith(',') || inputText.endsWith(', ')
  }, [inputText, filterOnlySearch])

  // 검색 필터링된 옵션들
  const filteredOptions = useMemo(() => {
    // 콤마로 끝나거나 검색어가 없으면 전체 옵션 표시
    if (!searchTerm || isAddingNew) return options

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) || String(option.value).toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [options, searchTerm, isAddingNew])

  // 선택된 값들을 Set으로 관리
  const selectedSet = useMemo(() => new Set(values.map((v) => String(v))), [values])

  // 전체 선택 여부 체크
  const isAllSelected = useMemo(() => {
    if (onlySelectAllMode) return true
    if (filteredOptions.length === 0) return false
    return filteredOptions.every((option) => selectedSet.has(String(option.value)))
  }, [filteredOptions, selectedSet, onlySelectAllMode])

  // onlySelectAllMode가 true일 때 모든 옵션 선택
  useEffect(() => {
    if (onlySelectAllMode && options.length > 0 && onChange) {
      const allValues = options.map((option) => option.value)
      // 현재 선택된 값과 모든 값이 같은지 확인
      const currentValueSet = new Set(values.map((v) => String(v)))
      const allValueSet = new Set(allValues.map((v) => String(v)))

      // 집합의 크기가 다르거나 모든 값이 포함되지 않은 경우에만 onChange 호출
      if (currentValueSet.size !== allValueSet.size || !allValues.every((v) => currentValueSet.has(String(v)))) {
        onChange(allValues)
      }
    }
  }, [onlySelectAllMode, options, values, onChange])

  // 입력 텍스트 변경 시 선택된 값 동기화 (검색 전용 모드에서는 입력이 선택을 바꾸지 않음)
  useEffect(() => {
    if (filterOnlySearch) return
    if (!inputText.trim()) {
      return
    }

    // 입력된 텍스트를 분석하여 현재 유효한 선택 값들만 필터링
    const inputValues = inputText.split(',').map((text) => text.trim())
    const validValues: T[] = []

    inputValues.forEach((inputValue) => {
      // 옵션에서 해당 라벨을 가진 값 찾기
      const matchedOption = options.find((option) => option.label.toLowerCase() === inputValue.toLowerCase())
      if (matchedOption) {
        validValues.push(matchedOption.value)
      }
    })

    // 현재 선택된 값과 다르면 업데이트
    const currentValueStrings = values.map((v) => String(v)).sort()
    const validValueStrings = validValues.map((v) => String(v)).sort()

    if (currentValueStrings.join(',') !== validValueStrings.join(',')) {
      onChange?.(validValues)
    }
  }, [inputText, options, values, onChange, filterOnlySearch])

  // 입력 필드 변경 핸들러
  const handleInputChange = useCallback(
    (value: string) => {
      setInputText(value)
      setIsOpen(true)
      onInputChange?.(value)
    },
    [onInputChange],
  )

  // 커서를 맨 끝으로 이동
  const moveCursorToEnd = useCallback(() => {
    const input = inputRef.current
    if (input) {
      const length = input.value.length
      input.setSelectionRange(length, length)
    }
  }, [])

  // displayText 변경 시 커서를 맨 끝으로 이동
  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      moveCursorToEnd()
    }
  }, [displayText, moveCursorToEnd])

  // 키다운 처리
  // - Enter: 상위 폼(FilterListV2) 암묵 제출로 '전체 필터' 패널이 열리는 것 방지 후 검색 트리거
  // - Backspace: 입력란이 비어(선택 라벨만 표시) 있을 때 마지막 선택을 해제 (칩 삭제 UX).
  //   선택 라벨이 input 값으로 보여, 백스페이스로 글자를 지워 검색어가 비면 selectedText 로
  //   다시 폴백되어 선택이 재표시되던 문제를 방지한다.
  useEffect(() => {
    const input = inputRef.current
    if (!input) return undefined
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.isComposing) return
      if (e.key === 'Enter') {
        e.preventDefault()
        onEnter?.()
        return
      }
      if (e.key === 'Backspace' && inputText === '' && values.length > 0) {
        e.preventDefault()
        onChange?.(values.slice(0, -1))
      }
    }
    input.addEventListener('keydown', handleKeyDown)
    return () => input.removeEventListener('keydown', handleKeyDown)
  }, [onEnter, inputText, values, onChange])

  // 외부 클릭 시 드롭다운 닫기
  const handleClickOutside = useCallback(() => {
    setIsOpen(false)
    setInputText('') // 드롭다운 닫힐 때 입력 텍스트 초기화
  }, [])

  // 포털 모드에서는 드롭다운이 containerRef 밖(body)에 있으므로 dropdownRef 도 함께 예외 처리
  useClickOutside([containerRef as RefObject<HTMLDivElement>, dropdownRef], handleClickOutside)

  // 포털 모드 — 트리거 위치 기준으로 드롭다운을 fixed 배치하고 스크롤/리사이즈 시 재계산
  const updatePortalPosition = useCallback(() => {
    if (!usePortal || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setPortalStyle({
      position: 'fixed',
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
      right: 'auto',
      zIndex: zIndexSystem.modal + 1,
    })
  }, [usePortal])

  useEffect(() => {
    if (!usePortal || !isOpen) return
    updatePortalPosition()
    // 리사이즈는 위치만 재계산
    const onResize = () => updatePortalPosition()
    // 스크롤 시에는 드롭다운을 닫는다.
    // fixed 배치가 트리거를 따라가는데, 트리거가 sticky 헤더 뒤로 숨어도
    // 드롭다운은 화면에 그대로 떠 영역을 벗어나 보이기 때문. 단, 드롭다운 자체
    // (옵션 리스트)의 내부 스크롤은 닫힘 대상에서 제외한다.
    const onScroll = (e: Event) => {
      const target = e.target as Node | null
      if (target && dropdownRef.current?.contains(target)) return
      setIsOpen(false)
      setInputText('')
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [usePortal, isOpen, updatePortalPosition])

  // 메뉴가 닫힐 때 options에 없는 값들 제거
  useEffect(() => {
    if (!isOpen && onChange && values.length > 0) {
      const validValues = values.filter((value) => options.some((option) => option.value === value))

      // 유효하지 않은 값이 있었다면 업데이트
      if (validValues.length !== values.length) {
        onChange(validValues)
      }
    }
  }, [isOpen, values, options, onChange])

  // 입력 필드 포커스 핸들러
  const handleInputFocus = useCallback(() => {
    setIsOpen(true)
    // 포커스 시 커서를 맨 끝으로 이동
    setTimeout(moveCursorToEnd, 0)
  }, [moveCursorToEnd])

  // 입력 필드 블러 핸들러 - 이제 드롭다운을 닫지 않고 포커스만 관리
  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // 드롭다운 내부를 클릭한 경우에는 포커스를 유지
      const relatedTarget = e.relatedTarget as HTMLElement
      if (relatedTarget && relatedTarget.closest('[data-dropdown-container]')) {
        // 포커스를 다시 input으로 되돌림
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus()
            moveCursorToEnd()
          }
        }, 0)
      }
      // 외부로 포커스가 이동해도 드롭다운은 열린 상태 유지
    },
    [moveCursorToEnd],
  )

  // 전체 선택/해제
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onChange || disableSelectAll) return

      if (checked) {
        if (filterOnlySearch) {
          // 검색 전용 모드: 검색 결과(필터된 옵션) 전체로 교체 — 이전 검색 선택 누적 방지
          onChange(filteredOptions.map((option) => option.value))
        } else {
          // 현재 필터된 옵션들을 기존 선택에 누적
          const newValues = [...values]
          filteredOptions.forEach((option) => {
            if (!selectedSet.has(String(option.value))) {
              newValues.push(option.value)
            }
          })
          onChange(newValues)
        }
      } else {
        // 현재 필터된 옵션들을 모두 해제
        const filteredValueSet = new Set(filteredOptions.map((opt) => String(opt.value)))
        onChange(values.filter((v) => !filteredValueSet.has(String(v))))
      }

      // 검색 전용 모드는 검색어를 유지해 연속 선택 가능, 아니면 입력 텍스트 초기화
      if (!filterOnlySearch) setInputText('')
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          moveCursorToEnd()
        }
      }, 0)
    },
    [values, filteredOptions, selectedSet, onChange, moveCursorToEnd, disableSelectAll, filterOnlySearch],
  )

  // 개별 옵션 선택/해제
  const handleOptionChange = useCallback(
    (checked: boolean, optionValue: T) => {
      if (!onChange) return

      if (checked) {
        onChange([...values, optionValue])
      } else {
        onChange(values.filter((v) => v !== optionValue))
      }

      // 검색 전용 모드는 검색어를 유지해 연속 선택 가능, 아니면 입력 텍스트 초기화
      if (!filterOnlySearch) setInputText('')
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          moveCursorToEnd()
        }
      }, 0)
    },
    [values, onChange, moveCursorToEnd, filterOnlySearch],
  )

  const optionsContent = (
    <>
      {/* 전체 선택 체크박스 - 검색 전용 모드는 항상(필터 결과 전체 선택), 아니면 콤마로 끝나거나 검색어 없을 때만 */}
      {(filterOnlySearch || !searchTerm || isAddingNew) && filteredOptions.length > 0 && (
        <SelectAllOption
          onClick={(e) => {
            // 체크박스 자체를 클릭한 경우에는 중복 실행 방지
            const target = e.target as HTMLElement
            if (target.closest('.mwms-checkbox') || target.closest('label')) {
              return
            }
            handleSelectAll(!isAllSelected)
          }}
          disabled={onlySelectAllMode || disableSelectAll}
        >
          <Checkbox labelText="전체" disabled={onlySelectAllMode || disableSelectAll} checked={isAllSelected} onChange={handleSelectAll} size="md" />
        </SelectAllOption>
      )}

      {filteredOptions.length > 0 ? (
        filteredOptions.map((option) => (
          <OptionItem
            key={`${option.key || option.value}`}
            onClick={(e) => {
              // 체크박스 자체를 클릭한 경우에는 중복 실행 방지
              const target = e.target as HTMLElement
              if (target.closest('.mwms-checkbox') || target.closest('label')) {
                return
              }
              handleOptionChange(!selectedSet.has(String(option.value)), option.value)
            }}
            disabled={onlySelectAllMode || (option.disabledCheckValue !== undefined ? option.disabledCheckValue === option.value : false)}
          >
            <Checkbox
              labelText={option.label}
              checked={selectedSet.has(String(option.value))}
              onChange={(checked: boolean) => handleOptionChange(checked, option.value)}
              size="md"
              disabled={onlySelectAllMode || (option.disabledCheckValue !== undefined ? option.disabledCheckValue === option.value : false)}
            />
          </OptionItem>
        ))
      ) : (
        <EmptyMessage>검색 결과가 없습니다.</EmptyMessage>
      )}
    </>
  )

  const optionsNode = (
    <OptionsContainer ref={dropdownRef} $maxHeight={maxHeight} data-dropdown-container style={usePortal ? portalStyle : undefined}>
      {optionsContent}
    </OptionsContainer>
  )

  return (
    <Container ref={containerRef} className={className}>
      {/* 입력 필드 */}
      <SearchInput
        ref={inputRef}
        placeholder={placeholder}
        value={displayText}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        disabled={onlySelectAllMode || disabled}
        rightIcon={{ name: `arrow-${isOpen ? 'up' : 'down'}-filled` }}
      />

      {/* 옵션 리스트 */}
      {isOpen && (usePortal ? createPortal(optionsNode, document.body) : optionsNode)}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`

const SearchInput = styled(Input)<{ disabled?: boolean }>`
  .input-label {
    display: none;
  }

  input {
    border: 1px solid ${colorSystem.border.element};
    border-radius: 4px;
    color: ${({ disabled }) => (disabled ? colorSystem.text.common.disabled : colorSystem.text.common.primary)};

    &:focus {
      border: 1px solid ${colorSystem.border.element_active};
    }
  }
`

const OptionsContainer = styled(VStack)<{ $maxHeight: number }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow-y: auto;
  padding: 8px;
  gap: 4px;
  background-color: ${colors.white};
  border: 1px solid ${colorSystem.border.element};
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colorSystem.bg.common.secondary};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colorSystem.border.element};
    border-radius: 3px;

    &:hover {
      background: ${colorSystem.border.element_active};
    }
  }

  /* 체크박스 커서 스타일 */
  .mwms-checkbox {
    cursor: pointer;
  }

  label {
    cursor: pointer;
  }
`

const SelectAllOption = styled.div<{ disabled?: boolean }>`
  padding: 8px 4px;
  border-bottom: 1px solid ${colorSystem.border.divider};
  margin-bottom: 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${colorSystem.bg.common.secondary_hover};
  }

  .checkbox-label {
    font-weight: 500;
  }
`

const OptionItem = styled.div<{ disabled?: boolean }>`
  padding: 6px 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ disabled }) => (disabled ? 'transparent' : colorSystem.bg.common.secondary_hover)};
  }
`

const EmptyMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: ${colorSystem.text.common.secondary};
  font-size: 14px;
`

const SearchableMultipleSelect = forwardRef(SearchableMultipleSelectInner) as <T>(
  props: SearchableMultipleSelectProps<T> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof SearchableMultipleSelectInner>

export default SearchableMultipleSelect
